/**
 * App Job Validation Schemas
 *
 * Validation schemas for app job management operations.
 * App jobs define cron-based recurring execution of application functions
 * using service accounts for execution identity.
 */

import { z } from 'zod';
import { uuidSchema, descriptionSchema, entityCodeSchema } from './common';

// ============================================================================
// APP JOB FIELD VALIDATION
// ============================================================================

/**
 * App job code schema
 * Must be lowercase alphanumeric with hyphens only, 3-50 chars
 * Immutable after creation
 */
export const appJobCodeSchema = entityCodeSchema;

/**
 * App job name schema
 * Display name with uppercase, spaces, hyphens, and underscores allowed
 * 1-100 characters
 */
export const appJobNameSchema = z
  .string()
  .min(1, 'Name must be at least 1 character')
  .max(100, 'Name cannot exceed 100 characters')
  .regex(
    /^[a-zA-Z0-9 _-]+$/,
    'Name can contain letters, numbers, spaces, hyphens, and underscores'
  );

/**
 * Function name schema
 * Must be a valid JavaScript function name
 */
export const functionNameSchema = z
  .string()
  .min(1, 'Function name is required')
  .max(100, 'Function name cannot exceed 100 characters')
  .regex(
    /^[a-zA-Z_$][a-zA-Z0-9_$]*$/,
    'Function name must be a valid JavaScript identifier'
  );

/**
 * Cron expression schema
 * Standard 5-field cron format: minute hour day-of-month month day-of-week
 * Basic format validation only (full validation done server-side)
 */
export const cronExpressionSchema = z
  .string()
  .min(9, 'Cron expression is required')
  .max(100, 'Cron expression cannot exceed 100 characters');

/**
 * Timezone schema
 * IANA timezone identifier (e.g., "America/New_York", "UTC")
 */
export const timezoneSchema = z
  .string()
  .min(1, 'Timezone is required')
  .max(50, 'Timezone cannot exceed 50 characters');

/**
 * Payload schema (JSON object)
 */
export const payloadSchema = z
  .record(z.unknown())
  .optional();

// ============================================================================
// APP JOB CREATE VALIDATION
// ============================================================================

/**
 * App job creation schema
 */
export const appJobCreateSchema = z.object({
  appJobCode: appJobCodeSchema,
  appJobName: appJobNameSchema,
  description: descriptionSchema,
  serviceAccountId: uuidSchema,
  functionName: functionNameSchema,
  payload: payloadSchema,
  cronExpression: cronExpressionSchema,
  timezone: timezoneSchema,
});

export type AppJobCreateFormData = z.infer<typeof appJobCreateSchema>;

/**
 * Form-specific app job creation schema
 * Uses string for payload input (JSON string)
 */
export const createAppJobFormSchema = z.object({
  applicationId: uuidSchema,
  appJobCode: appJobCodeSchema,
  appJobName: appJobNameSchema,
  description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
  serviceAccountId: uuidSchema,
  functionName: functionNameSchema,
  payloadInput: z.string().max(10000, 'Payload too long').optional(),
  cronExpression: cronExpressionSchema,
  timezone: timezoneSchema,
});

export type CreateAppJobFormData = z.infer<typeof createAppJobFormSchema>;

// ============================================================================
// APP JOB UPDATE VALIDATION
// ============================================================================

/**
 * App job update schema
 * Note: appJobCode is immutable and cannot be updated
 */
export const appJobUpdateSchema = z.object({
  appJobName: appJobNameSchema.optional(),
  description: descriptionSchema.optional(),
  functionName: functionNameSchema.optional(),
  payload: payloadSchema,
  cronExpression: cronExpressionSchema.optional(),
  timezone: timezoneSchema.optional(),
});

export type AppJobUpdateFormData = z.infer<typeof appJobUpdateSchema>;

/**
 * Form-specific app job update schema
 */
export const updateAppJobFormSchema = z.object({
  appJobName: appJobNameSchema.optional(),
  description: z.string().max(1000, 'Description must not exceed 1000 characters').optional(),
  functionName: functionNameSchema.optional(),
  payloadInput: z.string().max(10000, 'Payload too long').optional(),
  cronExpression: cronExpressionSchema.optional(),
  timezone: timezoneSchema.optional(),
});

export type UpdateAppJobFormData = z.infer<typeof updateAppJobFormSchema>;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Parse JSON payload from string input
 * @param input - JSON string
 * @returns Parsed object or undefined
 * @throws Error if invalid JSON
 */
export function parsePayload(input: string): Record<string, unknown> | undefined {
  const trimmed = input.trim();
  if (!trimmed) {
    return undefined;
  }
  try {
    const parsed = JSON.parse(trimmed);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      throw new Error('Payload must be a JSON object');
    }
    return parsed;
  } catch (error) {
    if (error instanceof SyntaxError) {
      throw new Error('Invalid JSON format');
    }
    throw error;
  }
}

/**
 * Format payload for display
 * @param payload - Payload object
 * @returns Formatted JSON string
 */
export function formatPayload(payload: Record<string, unknown> | undefined): string {
  if (!payload) {
    return '';
  }
  return JSON.stringify(payload, null, 2);
}

/**
 * Describe a cron expression in human-readable format
 * Note: This is a simplified version - for full descriptions, use a library like cronstrue
 * @param cronExpression - Cron expression string
 * @returns Human-readable description
 */
export function describeCronExpression(cronExpression: string): string {
  const parts = cronExpression.trim().split(/\s+/);
  if (parts.length !== 5) {
    return cronExpression;
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Common patterns
  if (minute === '0' && hour === '0' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return 'Daily at midnight';
  }
  if (minute === '0' && dayOfMonth === '*' && month === '*' && dayOfWeek === '*') {
    return `Daily at ${hour}:00`;
  }
  if (dayOfMonth === '*' && month === '*' && dayOfWeek === '1') {
    return `Every Monday at ${hour}:${minute.padStart(2, '0')}`;
  }
  if (dayOfMonth === '1' && dayOfWeek === '*') {
    return `Monthly on the 1st at ${hour}:${minute.padStart(2, '0')}`;
  }
  if (minute === '*/5') {
    return 'Every 5 minutes';
  }
  if (minute === '*/15') {
    return 'Every 15 minutes';
  }
  if (minute === '*/30') {
    return 'Every 30 minutes';
  }
  if (minute === '0' && hour === '*') {
    return 'Every hour';
  }

  return cronExpression;
}

/**
 * Common timezone options
 */
export const commonTimezones = [
  { value: 'UTC', label: 'UTC' },
  { value: 'America/New_York', label: 'Eastern Time (US)' },
  { value: 'America/Chicago', label: 'Central Time (US)' },
  { value: 'America/Denver', label: 'Mountain Time (US)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (US)' },
  { value: 'America/Anchorage', label: 'Alaska Time (US)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (US)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

/**
 * Common cron presets
 */
export const cronPresets = [
  { value: '0 0 * * *', label: 'Daily at midnight' },
  { value: '0 9 * * *', label: 'Daily at 9:00 AM' },
  { value: '0 0 * * 0', label: 'Weekly on Sunday' },
  { value: '0 0 * * 1', label: 'Weekly on Monday' },
  { value: '0 0 1 * *', label: 'Monthly on the 1st' },
  { value: '*/5 * * * *', label: 'Every 5 minutes' },
  { value: '*/15 * * * *', label: 'Every 15 minutes' },
  { value: '*/30 * * * *', label: 'Every 30 minutes' },
  { value: '0 * * * *', label: 'Every hour' },
];
